// Category management — categories come from the cached useCategories() hook;
// create/update/delete invalidate the ["categories"] query to refresh.
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { COLORS } from "../theme/tokens";
import FlatCard from "../components/FlatCard";
import { HEADING_FONT } from "../theme/fonts";
import api from "../api";
import { useCategories } from "../hooks/queries";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import { useTableSelection } from "../hooks/useTableSelection";
import SelectionActionBar from "../components/SelectionActionBar";
import { AuthContext } from "../context/AuthContext";

// Spec palette (blue · light) — sourced from the shared design tokens.
const BLUE = COLORS.primary;
const BLUE_SOFT = COLORS.primarySoft;

function Category() {
  // Flat spec design tokens (light-only). Replaces the old glass theme
  // (useThemeColors) so the page is locked to the "Blue · Light" system.
  const colors = {
    text: COLORS.ink,
    textMuted: COLORS.muted,
    textFaint: COLORS.faint,
    cardBg: COLORS.surface,
    cardBorder: COLORS.hairline,
    cardBorderHover: COLORS.borderHover,
    hover: COLORS.tile,
    divider: COLORS.hairline,
  };
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const { hasPermission } = useContext(AuthContext);
  const canAdd = hasPermission("categories", "add");
  const canEdit = hasPermission("categories", "edit");
  const canDelete = hasPermission("categories", "delete");

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  // Edit dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");

  // View dialog
  const [viewTarget, setViewTarget] = useState(null);

  // Categories come from the shared, cached React Query hook.
  const { data: categoriesData } = useCategories();
  const categories = categoriesData || [];

  // --- Add ---

  function openAddDialog() {
    setNewName("");
    setAddOpen(true);
  }

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post("/categories", { name });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      setAddOpen(false);
      setNewName("");
      toast.success("Category added");
    } catch (err) {
      toast.error(err.message || "Failed to add category");
    } finally {
      setSaving(false);
    }
  }

  // --- Edit ---

  function openEditDialog(cat) {
    setEditTarget(cat);
    setEditName(cat.name);
  }

  async function handleUpdate() {
    const name = editName.trim();
    if (!name || !editTarget) return;
    setSaving(true);
    try {
      await api.put(`/categories/${editTarget.id}`, { name });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      setEditTarget(null);
      toast.success("Category updated");
    } catch (err) {
      toast.error(err.message || "Failed to update category");
    } finally {
      setSaving(false);
    }
  }

  // --- Delete ---

  async function handleDelete(id) {
    try {
      await api.delete(`/categories/${id}`);
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete category");
    }
  }

  // --- Row selection + bulk delete ---
  const pageIds = categories.map((c) => c.id);
  const {
    selectedIds,
    toggleRow,
    toggleAll,
    clear: clearSelection,
    allSelected,
    someSelected,
  } = useTableSelection(pageIds);
  const [bulkBusy, setBulkBusy] = useState(false);

  async function handleDeleteSelected() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} categor${ids.length === 1 ? "y" : "ies"}?`,
      message: "The selected categories will be permanently removed.",
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/categories/bulk-delete", { ids });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? ids.length} categor(y/ies)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete selected categories");
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleDeleteAll() {
    const ok = await confirm({
      title: "Delete ALL categories?",
      message:
        "Every category will be permanently removed. This cannot be undone.",
      danger: true,
      confirmLabel: "Delete all",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/categories/delete-all");
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? 0} categor(y/ies)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete all categories");
    } finally {
      setBulkBusy(false);
    }
  }

  function openCategoryItems(cat) {
    const name = cat?.name ? encodeURIComponent(cat.name) : "";
    navigate(name ? `/domains?category=${name}` : "/domains");
  }

  const labelSx = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: colors.text,
    mb: 1,
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: "#ffffff",
      color: colors.text,
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
    },
    "& input": { color: colors.text },
  };

  // Leading 44px track is the checkbox column.
  // Added a View action column so Actions is now View + Edit + Delete.
  const GRID_COLUMNS = "44px 70px 1fr 180px 160px";

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              color: colors.text,
              letterSpacing: "-0.5px",
            }}
          >
            Categories
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
            {categories.length}{" "}
            {categories.length === 1 ? "category" : "categories"} defined
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ alignSelf: { xs: "flex-end", sm: "center" }, ml: { sm: "auto" } }}
        >
          {canDelete && selectedIds.size > 0 && (
            <Button
              onClick={handleDeleteAll}
              startIcon={<DeleteOutlineIcon />}
              disabled={bulkBusy}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 2,
                py: 1.1,
                borderRadius: "10px",
                color: "#DC2626",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.25)",
                "&:hover": { background: "rgba(239, 68, 68, 0.18)" },
                transition: "all 0.15s ease",
              }}
            >
              Delete All
            </Button>
          )}

          {canAdd && (
          <Button
            onClick={openAddDialog}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              px: 2.5,
              py: 1.1,
              borderRadius: "10px",
              color: "#fff",
              background: "#3B82F6",
              boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
              "&:hover": {
                boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.15s ease",
            }}
          >
            Add Category
          </Button>
          )}
        </Stack>
      </Stack>

      {/* Bulk-delete selection bar */}
      <SelectionActionBar
        count={selectedIds.size}
        onDelete={canDelete ? handleDeleteSelected : undefined}
        onClear={clearSelection}
        busy={bulkBusy}
        colors={colors}
      />

      {/* Table */}
      <FlatCard sx={{ overflow: "hidden" }}>
        {/* Horizontal scroll wrapper — full table on mobile, swipe sideways */}
        <Box sx={{ overflowX: "auto" }}>
        {/* Table header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: GRID_COLUMNS,
            minWidth: { xs: "max-content", md: "auto" },
            gap: 2,
            px: 3,
            py: 1.75,
            borderBottom: `1px solid ${colors.divider}`,
            background: colors.hover,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
              sx={{ p: 0, color: colors.textMuted }}
            />
          </Box>
          {["#", "Category Name", "Domains", "Actions"].map((h) => (
            <Typography
              key={h}
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: colors.textMuted,
                textAlign: "left",
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {/* Rows */}
        {categories.length === 0 ? (
          <Box sx={{ py: 8, px: 3, textAlign: "center" }}>
            <CategoryOutlinedIcon
              sx={{ fontSize: 44, color: colors.textFaint, mb: 1 }}
            />
            <Typography
              sx={{ fontFamily: HEADING_FONT, fontSize: 15, fontWeight: 600, color: colors.text }}
            >
              No categories yet
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.textMuted, mt: 0.5 }}>
              Click "Add Category" to create your first one
            </Typography>
          </Box>
        ) : (
          categories.map((cat, idx) => (
            <Box
              key={cat.id}
              sx={{
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                minWidth: { xs: "max-content", md: "auto" },
                gap: 2,
                px: 3,
                py: 1.75,
                alignItems: "center",
                borderBottom:
                  idx < categories.length - 1
                    ? `1px solid ${colors.divider}`
                    : "none",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "3px",
                  background: BLUE,
                  opacity: 0,
                  transition: "opacity 0.2s ease",
                },
                "&:hover": { background: "#F8FAFC" },
                "&:hover::before": { opacity: 1 },
                transition: "background 0.18s ease",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={selectedIds.has(cat.id)}
                  onChange={() => toggleRow(cat.id)}
                  sx={{ p: 0, color: colors.textMuted }}
                />
              </Box>
              <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}>
                {idx + 1}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    background: BLUE_SOFT,
                    color: BLUE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CategoryOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography
                  sx={{ fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 600, color: colors.text }}
                >
                  {cat.name}
                </Typography>
              </Stack>
              <Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => openCategoryItems(cat)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.4,
                    borderRadius: "999px",
                    border: "none",
                    background: (cat.domainCount || 0) + (cat.subdomainCount || 0) > 0 ? BLUE_SOFT : colors.hover,
                    color: (cat.domainCount || 0) + (cat.subdomainCount || 0) > 0 ? BLUE : colors.textMuted,
                    fontFamily: HEADING_FONT,
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "none",
                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 16px rgba(59, 130, 246, 0.16)",
                    },
                  }}
                >
                  {(cat.domainCount || 0) + (cat.subdomainCount || 0)}
                  <Box component="span" sx={{ fontWeight: 500, opacity: 0.85 }}>
                    {((cat.domainCount || 0) + (cat.subdomainCount || 0)) === 1 ? "item" : "items"}
                  </Box>
                </Box>
              </Box>
              {/* Actions — View · Edit · Delete (same pattern as DomainList) */}
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title="View">
                  <IconButton
                    size="small"
                    onClick={() => setViewTarget(cat)}
                    sx={{
                      width: 32, height: 32, borderRadius: 1.5,
                      color: "#0ea5e9",
                      background: "rgba(14, 165, 233, 0.1)",
                      border: "1px solid rgba(14, 165, 233, 0.25)",
                      "&:hover": { background: "rgba(14, 165, 233, 0.18)" },
                    }}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => openEditDialog(cat)}
                      sx={{
                        width: 32, height: 32, borderRadius: 1.5,
                        color: "#3B82F6",
                        background: "rgba(59, 130, 246, 0.1)",
                        border: "1px solid rgba(59, 130, 246, 0.25)",
                        "&:hover": { background: "rgba(59, 130, 246, 0.18)" },
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={async () => {
                        if (
                          await confirm({
                            title: "Delete category?",
                            message: "This action cannot be undone.",
                            danger: true,
                            confirmLabel: "Delete",
                          })
                        )
                          handleDelete(cat.id);
                      }}
                      sx={{
                        width: 32, height: 32, borderRadius: 1.5,
                        color: "#DC2626",
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "1px solid rgba(239, 68, 68, 0.25)",
                        "&:hover": { background: "rgba(239, 68, 68, 0.18)" },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          ))
        )}
        </Box>
      </FlatCard>

      {/* ── Category View Modal ───────────────────────────────────────── */}
      <Dialog
        open={viewTarget !== null}
        onClose={() => setViewTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            background: "#ffffff",
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        {/* Blue gradient banner */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.75,
            px: 3,
            py: 2.5,
            color: "#fff",
            background: `linear-gradient(135deg, #1D4ED8 0%, ${BLUE} 100%)`,
          }}
        >
          <Box
            sx={{
              width: 46, height: 46, borderRadius: "12px",
              background: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CategoryOutlinedIcon sx={{ fontSize: 25 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1.4px", textTransform: "uppercase", opacity: 0.85 }}>
              Category
            </Typography>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 21, fontWeight: 700, letterSpacing: "-0.4px", lineHeight: 1.15 }}>
              {viewTarget?.name || ""}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setViewTarget(null)}
            sx={{ color: "rgba(255,255,255,0.9)", "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ px: 3, py: 3 }}>
          {/* Combined count badge */}
          <Box
            component="button"
            type="button"
            onClick={() => openCategoryItems(viewTarget)}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%",
              p: 2, borderRadius: "12px",
              border: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
              cursor: "pointer",
              textAlign: "left",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 8px 20px rgba(59, 130, 246, 0.12)",
              },
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: colors.textMuted, mb: 0.5 }}>
                Domains + subdomains using this category
              </Typography>
              <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 22, fontWeight: 700, color: ((viewTarget?.domainCount || 0) + (viewTarget?.subdomainCount || 0)) > 0 ? BLUE : colors.textMuted }}>
                {(viewTarget?.domainCount || 0) + (viewTarget?.subdomainCount || 0)}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 48, height: 48, borderRadius: "12px",
                background: ((viewTarget?.domainCount || 0) + (viewTarget?.subdomainCount || 0)) > 0 ? BLUE_SOFT : colors.tile,
                color: ((viewTarget?.domainCount || 0) + (viewTarget?.subdomainCount || 0)) > 0 ? BLUE : colors.textFaint,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <CategoryOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
          </Box>

          {/* Status badge row */}
          <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
            <Box
              component="span"
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                px: 1.5, py: 0.6, borderRadius: "999px",
                fontFamily: HEADING_FONT, fontWeight: 700, fontSize: 11,
                color: viewTarget?.domainCount > 0 ? "#16A34A" : "#DC2626",
                background: viewTarget?.domainCount > 0 ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)",
              }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: viewTarget?.domainCount > 0 ? "#16A34A" : "#DC2626", flexShrink: 0 }} />
              {viewTarget?.domainCount > 0 ? "In Use" : "Unused"}
            </Box>
          </Box>
        </Box>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            onClick={() => setViewTarget(null)}
            sx={{
              textTransform: "none", fontWeight: 600, px: 3,
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
            }}
          >
            Close
          </Button>
          {canEdit && (
            <Button
              onClick={() => { setViewTarget(null); openEditDialog(viewTarget); }}
              sx={{
                textTransform: "none", fontWeight: 600, px: 3,
                color: "#fff", background: BLUE,
                "&:hover": { boxShadow: "0 8px 22px rgba(59,130,246,0.45)" },
              }}
            >
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add dialog */}
      <Dialog
        open={addOpen}
        onClose={() => !saving && setAddOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: "#ffffff",
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        {/* Blue gradient header banner */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.75,
            px: 3,
            py: 2.5,
            color: "#fff",
            background: `linear-gradient(135deg, #1D4ED8 0%, ${BLUE} 100%)`,
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "12px",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CategoryOutlinedIcon sx={{ fontSize: 25 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1.4px", textTransform: "uppercase", opacity: 0.85 }}>
              New Category
            </Typography>
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: 21,
                fontWeight: 700,
                letterSpacing: "-0.4px",
                lineHeight: 1.15,
              }}
            >
              Add Category
            </Typography>
          </Box>
          <IconButton
            onClick={() => !saving && setAddOpen(false)}
            aria-label="Close"
            sx={{ color: "rgba(255,255,255,0.9)", "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <Box sx={{ mt: 1 }}>
            <Typography component="label" sx={labelSx}>
              Category Name
            </Typography>
            <TextField
              fullWidth
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              placeholder="e.g. Real Estate"
              sx={fieldSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${colors.divider}` }}>
          <Button
            onClick={() => setAddOpen(false)}
            disabled={saving}
            sx={{
              textTransform: "none",
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!newName.trim() || saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              color: "#fff",
              background: "#3B82F6",
              "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)" },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.6)",
                background: colors.cardBorder,
              },
            }}
          >
            {saving ? "Adding…" : "Add Category"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={editTarget !== null}
        onClose={() => !saving && setEditTarget(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: "#ffffff",
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: HEADING_FONT,
            color: colors.text,
            fontWeight: 700,
            borderBottom: `1px solid ${colors.divider}`,
          }}
        >
          Edit Category
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <Box sx={{ mt: 1 }}>
            <Typography component="label" sx={labelSx}>
              Category Name
            </Typography>
            <TextField
              fullWidth
              autoFocus
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
              sx={fieldSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${colors.divider}` }}>
          <Button
            onClick={() => setEditTarget(null)}
            disabled={saving}
            sx={{
              textTransform: "none",
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={!editName.trim() || saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              color: "#fff",
              background: "#3B82F6",
              "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)" },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.6)",
                background: colors.cardBorder,
              },
            }}
          >
            {saving ? "Saving…" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Category;
